import {
  ConcreteGranularity,
  TimeRangeInput,
  TimeSeriesGranularity,
} from 'dashboards/types/time';

const GRANULARITY_MINUTES: Record<ConcreteGranularity, number> = {
  MINUTE: 1,
  FIVE_MINUTES: 5,
  TEN_MINUTES: 10,
  FIFTEEN_MINUTES: 15,
  HOUR: 60,
  DAY: 60 * 24,
  WEEK: 60 * 24 * 7,
  MONTH: 60 * 24 * 30,
  YEAR: 60 * 24 * 365,
};

// Aim for a chart that has enough data points to feel rich, but not so many
// that bars/labels become unreadable.
const TARGET_BUCKETS = 50;
const MIN_BUCKETS = 12;
const MAX_BUCKETS = 120;

/**
 * Resolves 'AUTO' granularity to a concrete value based on the time range,
 * targeting roughly TARGET_BUCKETS data points on the resulting chart.
 *
 * `allowedGranularities` can be passed to constrain the choice (e.g. heatmaps
 * only support HOUR and DAY).
 */
export function resolveGranularity(
  granularity: TimeSeriesGranularity,
  timeRange: TimeRangeInput,
  allowedGranularities?: [ConcreteGranularity, ...ConcreteGranularity[]]
): ConcreteGranularity {
  if (granularity !== 'AUTO') {
    return granularity;
  }

  const diffMinutes =
    (new Date(timeRange.stop).getTime() -
      new Date(timeRange.start).getTime()) /
    60000;

  const allowed =
    allowedGranularities ??
    (Object.keys(GRANULARITY_MINUTES) as ConcreteGranularity[]);

  const candidates = allowed.map((g) => ({
    g,
    count: diffMinutes / GRANULARITY_MINUTES[g],
  }));

  // Prefer candidates whose bucket count lies in the readable range,
  // picking the one closest to the target.
  const inRange = candidates.filter(
    ({ count }) => count >= MIN_BUCKETS && count <= MAX_BUCKETS
  );

  const pool = inRange.length > 0 ? inRange : candidates;

  return pool.sort(
    (a, b) =>
      Math.abs(a.count - TARGET_BUCKETS) - Math.abs(b.count - TARGET_BUCKETS)
  )[0].g;
}
